// The `evidence/source` auto-evidence config READ, plus the two module-scope
// cache commands that live beside it — carved from
// `src/hooks/query/useQueryEvidenceConfig.ts` (now a bodied delegate).
//
// Two severances land here, and only one of them is mechanical.
//
// S-enabled (cohort S2 + the flag ledger). The pre-move gate was
// `(IS_TRANSCRIPT_CHIPS_ENABLED() || IS_ASK_HEIDI_SHORTCUTS_ENABLED()) && !!token`
// — two flag getters OR-ed together, AND-ed with a Rematch auth read, all
// composed inline inside a data hook. Every term is an app fact and none may
// enter a headless zone, so they collapse into the single caller-computed
// boolean this hook takes: the same severance this package shipped for the three
// source-list reads, and the shape the flag ledger specifies for a flag that
// gates a data read: the resolved boolean crosses as an explicit `enabled`
// input. The
// package never learns that a flag key or an auth store exists.
//
// The OR is preserved app-side rather than being modelled here as two inputs.
// It is EVI-2114 behaviour — config owns pinned shortcuts as well as chip
// categories, so killing transcript chips must not empty the pins — and a
// two-field options object would re-teach this package the legacy structure and
// let a future caller pass the pair in a combination the app cannot produce.
//
// S3, the queryClient severance. `getCachedEvidenceConfig` and
// `invalidateEvidenceConfig` are NOT hooks: they read and evict the cache from
// module scope, and `invalidateEvidenceConfig` is called from outside React
// entirely (the team-shortcut write fires it from a mutation callback, and that
// call stays app-side permanently because `workspace` is FOUNDATIONAL and can
// take no product-domain value import). There is no `useQueryClient()` in reach,
// so the app's singleton crosses the `configureEvidenceData` seam. Same
// instance, same cache, same timing.
//
// THE ERROR SWALLOW IS FROZEN BEHAVIOUR. `queryFn` catches, logs and returns
// `DEFAULT_EVIDENCE_CONFIG`, so every consumer sees `isSuccess` with the default
// rather than `isError`. The chip rail and the shortcut list render off `data`;
// a rethrow here would surface an error state those surfaces have never had to
// draw. Likewise `invalidateEvidenceConfig` swallows its own rejection and logs
// — the team-shortcut write is not allowed to fail because a downstream refresh
// did.
//
// `logger` crosses as the SAME SINK rather than as a port: `@/utils/logger/logger`
// is a pure runtime re-export of `@heidi/runtime/logger` (Wave 1 · P1), so
// importing the real module here is not a severance at all.
import { useQuery } from '@tanstack/react-query';

import { logger } from '@heidi/runtime/logger';

import {
  DEFAULT_EVIDENCE_CONFIG,
  GET_EVIDENCE_CONFIG,
  type AutoEvidenceConfig,
} from '../../contracts';
import { transcriptChipsApi } from '../api/transcript-chips';
import { getEvidenceDataConfig } from '../config';

/**
 * Read the config already in cache, without subscribing.
 *
 * Returns `undefined` when nothing is cached — the pre-move contract, and the
 * reason callers treat it as "best effort" rather than as the config.
 */
export const getCachedEvidenceConfig = (): AutoEvidenceConfig | undefined =>
  getEvidenceDataConfig().queryClient.getQueryData<AutoEvidenceConfig>([
    GET_EVIDENCE_CONFIG,
  ]);

/**
 * Evict the config cache so the next read refetches.
 *
 * Swallows and logs its own failure — see the header: the team-shortcut write
 * that fires this must not fail because a downstream refresh did.
 */
export const invalidateEvidenceConfig = async (): Promise<void> => {
  try {
    await getEvidenceDataConfig().queryClient.invalidateQueries({
      queryKey: [GET_EVIDENCE_CONFIG],
    });
  } catch (error) {
    logger.error('Failed to invalidate evidence config query:', error);
  }
};

export interface EvidenceConfigReadOptions {
  /**
   * App-resolved gate, already reduced to one decision.
   *
   * The delegate computes
   * `(IS_TRANSCRIPT_CHIPS_ENABLED() || IS_ASK_HEIDI_SHORTCUTS_ENABLED()) && !!token`
   * and this hook takes the answer. `false` suppresses the request exactly as
   * the token-absent or both-flags-off path did.
   *
   * `boolean` and not `boolean | undefined`, unlike the source-list reads' gate,
   * and the difference is measured rather than stylistic: those hooks composed
   * `!!token && IS_EVIDENCE_LIBRARY_ENABLED()`, whose PostHog getter returns
   * `undefined` while the client loads, so `undefined` had to cross verbatim to
   * preserve react-query's enabled-by-default reading of it. The two getters
   * here are `NEXT_PUBLIC_*`-and-Flagsmith booleans read through
   * `IS_*_ENABLED()` helpers that already coerce, and the pre-move expression
   * `shouldFetchConfig && !!token` could therefore only ever be `true` or
   * `false`. Widening the type would invent a third state the app cannot
   * produce.
   */
  enabled: boolean;
}

/**
 * Hook to fetch user's Evidence configuration
 *
 * @param select - Optional selector to transform the data
 * @param options - App-resolved gate; see {@link EvidenceConfigReadOptions}
 * @returns Query result with Evidence config
 */
export function useQueryEvidenceConfig<T = AutoEvidenceConfig>(
  select: ((data: AutoEvidenceConfig) => T) | undefined,
  { enabled }: EvidenceConfigReadOptions
) {
  return useQuery({
    queryKey: [GET_EVIDENCE_CONFIG],
    select,
    queryFn: async () => {
      try {
        const config = await transcriptChipsApi.getConfig();
        return config;
      } catch (error) {
        logger.error('Failed to fetch evidence config:', error);
        // Return default config on error so UI still works
        return DEFAULT_EVIDENCE_CONFIG;
      }
    },
    enabled,
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 30, // 30 minutes
  });
}
